import * as moment from 'moment';
import 'moment-timezone';
import { IPendingSwap } from '@nkr/models/index';
import { PendingSwapsActionItem, SwapsToShow } from '../models';
import { SwapCategory, PendingSwapsListType, FileDocumentType } from '@nkr/enums/index';
import { Button, OverlayTrigger, Popover, Badge, Glyphicon, Label } from 'react-bootstrap';
import { RecipientRowWrapper, LogisticsGpsWrapper } from './styles';
import { IActionButtons } from './action-buttons';
import Actions from './actions';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import DonorAlias from './donor-alias';
import RecipientAlias from './recipient-alias';
import CenterAlias from './center-alias';
import AlleleMatch from '@nkr/components/allele-match';

interface IProps {
    index: number
    centerId: number,
    data: IPendingSwap,
    type: PendingSwapsListType,
    widths: string[],
    show: SwapsToShow,
    onRequestScreeningXm: (swap: IPendingSwap, existingXm: boolean, status: string) => void
    onRequestFinalXm: (swap: IPendingSwap, existingXm: boolean, status: string, isInternalOrRemote: boolean) => void,
    onEmergencyNotification: (swap: IPendingSwap) => void,
    onRequestSerologyKit: (swap: IPendingSwap, isInternalOrRemote: boolean) => void,
    onUploadFinalXm: (swap: IPendingSwap) => void,
    onUploadSerology: (swap: IPendingSwap) => void,
    onViewLogistics: (swap: IPendingSwap) => void,
    onShowMap: (url: string) => void,
    onShowGps: (link: string) => void,
    onViewCustomLogistics: (chainId: number, clusterNumber: number, positionNumber: number) => void
    onGetFinalXmResults: (recipientId: number, donorId: number, fileName: string) => void,
    onDownloadFormsAndChecklists: (donorId: number, recipientId: number) => void,
    onGetFile: (docType: FileDocumentType, donorId: number) => void,
    onGetPatientOutcomes: (centerId: number) => void,
    onViewCtScan: (donorId: number) => void,
    readOnly?: boolean
}

const PendingSwapsRow = (props: IProps) => {
    const handleActionItemSelected = (eventKey: number) => {
        let url = '';
        let { donor, recipient } = props.data;

        switch (eventKey) {
            case PendingSwapsActionItem.PatientOutcomes:
                props.onGetPatientOutcomes(props.data.recipient.centerId);
                break;

            case PendingSwapsActionItem.RecipientAboFile:
                props.onGetFile(FileDocumentType.RecipientAbo, props.data.recipient.id);
                break;

            case PendingSwapsActionItem.RecipientDemoFile:
                props.onGetFile(FileDocumentType.RecipientDemo, props.data.recipient.id);
                break;

            case PendingSwapsActionItem.DonorHlaFile:
                props.onGetFile(FileDocumentType.DonorHistoTyping, props.data.donor.id);
                break;

            case PendingSwapsActionItem.RecipientHlaFile:
                props.onGetFile(FileDocumentType.RecipientHistoTyping, props.data.recipient.id);
                break;

            case PendingSwapsActionItem.DonorCtReport:
                props.onGetFile(FileDocumentType.DonorCTReport, props.data.donor.id);
                break;

            case PendingSwapsActionItem.DonorMedChart:
                props.onGetFile(FileDocumentType.DonorMedChart, props.data.donor.id);
                break;

            case PendingSwapsActionItem.DonorAboVerif:
                props.onGetFile(FileDocumentType.DonorVRLABO, props.data.donor.id);
                break;

            case PendingSwapsActionItem.ViewSerology:
                props.onGetFile(FileDocumentType.DonorSerology, props.data.donor.id);
                break;

            case PendingSwapsActionItem.UploadSerology:
            case PendingSwapsActionItem.ReuploadSerology:
                props.onUploadSerology(props.data);
                break;

            case PendingSwapsActionItem.Evaluate:
                if (props.data.matchHistoryId > 0)
                    url = `/center/${props.centerId}/recipient/evaluate-match/${props.data.matchHistoryId}`;
                else
                    url = `/center/${props.centerId}/recipient/${props.data.recipient.id}/evaluate-match/${props.data.donor.id}`;
                break;

            case PendingSwapsActionItem.UploadFinalXm:
                props.onUploadFinalXm(props.data);
                break;

            case PendingSwapsActionItem.RequestSerologyKit:
                props.onRequestSerologyKit(props.data, props.data.itemType == 'RD');
                break;

            case PendingSwapsActionItem.RequestScreeningXM:
                props.onRequestScreeningXm(props.data, props.data.existingScreeningXm, props.data.existingScreeningXmStatus);
                break;

            case PendingSwapsActionItem.RequestFinalXM:
                props.onRequestFinalXm(props.data, props.data.existingFinalXm, props.data.existingFinalXmStatus, props.type == PendingSwapsListType.Internal || props.data.itemType == 'RD');
                break;

            case PendingSwapsActionItem.EmergencyNotification:
                props.onEmergencyNotification(props.data);
                document.body.click();
                break;

            case PendingSwapsActionItem.FormsAndChecklists:
                props.onDownloadFormsAndChecklists(props.data.donor.id, props.data.recipient.id);
                break;

            case PendingSwapsActionItem.ViewFinalXm:
                props.onGetFinalXmResults(props.data.recipient.id, props.data.donor.id, '');
                break;

            case PendingSwapsActionItem.FinancialInfo:
                document.body.click();
                break;

            case PendingSwapsActionItem.DonorCtStudy:
                handleCtView(donor.id);
                break;

            case PendingSwapsActionItem.CenterAboVerification:
                props.onGetFile(FileDocumentType.DonorCTRABO, props.data.donor.id);
                break;

            case PendingSwapsActionItem.DonorAboMolecular:
                props.onGetFile(FileDocumentType.DonorHistoAbo, props.data.donor.id);
                break;
        }

        if (url != '') {
            window.location.href = url;
        }
    };

    const handleCtView = (donorId: number) => {
        props.onViewCtScan(donorId);
    };

    const handleViewCustomLogistics = () => {
        let { chainId, clusterNumber, positionNumber } = props.data;
        props.onViewCustomLogistics(chainId, clusterNumber, positionNumber);
    };

    const handleViewLogisticsPlan = () => {
        let { donor, recipient } = props.data;

        window.open(`/logistics/position/${donor.id}/${recipient.id}`, '_blank');
    };

    const handleViewLogistics = () => {
        props.onViewLogistics(props.data);
    };

    const handleShowMap = () => {
        props.onShowMap(props.data.mapLink);
    };

    const handleShowGps = () => {
        props.onShowGps(props.data.gpsAutomation.gpsLink);
    };

    const getHoursRemaining = () => {
        if (props.data.recipient != null) {
            if (props.data.recipient.centerId == props.centerId) {

                return Math.round(props.data.deadlineRemainingHours);
            }
        }

        return 0;
    };

    const getActionButtons = (): IActionButtons => {
        const swap = props.data;
        const isDonorCenter = props.centerId == swap.donor.centerId || props.centerId == swap.donor.rdnCenterId;
        const isRecipientCenter = props.centerId == swap.recipient.centerId;
        const showAddlSerologyButtonCriteria = isRecipientCenter && swap.category > SwapCategory.MatchOffer && props.type == PendingSwapsListType.Recipient;
        const isRddSwap = swap.itemType === "RDD" || swap.itemType === "KFL RDD";

        if (swap.category == SwapCategory.MatchOffer) {
            return {
                showPatientOutcomesButton: props.centerId == swap.donor.centerId,
                showAboFileButton: isDonorCenter && swap.recipient.aboUploadPath != '',
                showUploadFinalXmButton: isRecipientCenter,
                showViewFinalXmButton: swap.uploadedFinalXm,
                showDemographicsFileButton: isDonorCenter && swap.recipient.demographicsUploadPath != '',
                showHlaFileButton: swap.donor.histogeneticsPdfTypingFile != '',
                showRecipientHlaFileButton: swap.recipient.rawTypingUploadPath != '',
                showCtReportButton: swap.donor.ctReportUploadPath != '',
                showMedChartButton: swap.donor.medChartUploadPath != '',
                showAboVerifButton: swap.donor.vrlAboUploadPath != '',
                showViewSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '',

                showAddSerologyButton: (swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '') == false && !showAddlSerologyButtonCriteria,
                showReuploadSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '' && !showAddlSerologyButtonCriteria,

                showRequestScreeningXm: false,
                hasExistingScreeningXm: false,
                showRequestFinalXm: false,
                hasExistingFinalXm: false,
                canRequestFinalXm: false,
                showEmergencyNotification: false,
                showRequestSerologyKit: false,
                canRequestSerology: false,
                showDonorCtStudy: true,
                showCenterAboVerification: swap.donor.centerAboUploadPath != '',
                showDonorAboMolecular: swap.donor.histogeneticsPdfAboFile != '',
                showFormsAndChecklists: true,
                swap,
                centerId: props.centerId
            };
        }

        if (swap.category == SwapCategory.PendingCrossMatch) {
            return {
                showPatientOutcomesButton: false,

                showAboFileButton: isDonorCenter && swap.recipient.aboUploadPath != '',
                showDemographicsFileButton: isDonorCenter && swap.recipient.demographicsUploadPath != '',
                showRecipientHlaFileButton: swap.recipient.rawTypingUploadPath != '',
                showUploadFinalXmButton: isRecipientCenter,
                showViewFinalXmButton: swap.uploadedFinalXm,
                showHlaFileButton: swap.donor.histogeneticsPdfTypingFile != '',
                showCtReportButton: swap.donor.ctReportUploadPath != '',
                showMedChartButton: swap.donor.medChartUploadPath != '',
                showAboVerifButton: swap.donor.vrlAboUploadPath != '',

                showViewSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '',
                showAddSerologyButton: (swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '') == false && !showAddlSerologyButtonCriteria,
                showReuploadSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '' && !showAddlSerologyButtonCriteria,

                showRequestScreeningXm: false,
                hasExistingScreeningXm: false,
                showRequestFinalXm: isRddSwap ? swap.canRequestFinalXm : (isRecipientCenter && swap.canRequestFinalXm),
                hasExistingFinalXm: swap.existingFinalXm,
                canRequestFinalXm: swap.canRequestFinalXm,
                showEmergencyNotification: swap.recipient.isTransplanted == false,
                showRequestSerologyKit: swap.canRequestSerology,
                canRequestSerology: swap.canRequestSerology,
                showDonorCtStudy: true,
                showCenterAboVerification: swap.donor.centerAboUploadPath != '',
                showDonorAboMolecular: swap.donor.histogeneticsPdfAboFile != '',
                showFormsAndChecklists: true,
                swap,
                centerId: props.centerId
            };
        }

        if (swap.category == SwapCategory.CrossMatch) {
            return {
                showPatientOutcomesButton: isDonorCenter,

                showAboFileButton: isDonorCenter && swap.recipient.aboUploadPath != '',
                showDemographicsFileButton: isDonorCenter && swap.recipient.demographicsUploadPath != '',
                showRecipientHlaFileButton: swap.recipient.rawTypingUploadPath != '',
                showUploadFinalXmButton: isRecipientCenter,
                showViewFinalXmButton: swap.uploadedFinalXm,
                showHlaFileButton: swap.donor.histogeneticsPdfTypingFile != '',
                showCtReportButton: isRecipientCenter && swap.donor.ctReportUploadPath != '',
                showMedChartButton: isRecipientCenter && swap.donor.medChartUploadPath != '',
                showAboVerifButton: isRecipientCenter && swap.donor.vrlAboUploadPath != '',

                showViewSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '',
                showAddSerologyButton: (swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '') == false && !showAddlSerologyButtonCriteria,
                showReuploadSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '' && !showAddlSerologyButtonCriteria,

                showRequestScreeningXm: isRecipientCenter,
                hasExistingScreeningXm: swap.existingScreeningXm,

                showRequestFinalXm: isRddSwap ? swap.canRequestFinalXm : (isRecipientCenter && swap.canRequestFinalXm),
                hasExistingFinalXm: swap.existingFinalXm,
                canRequestFinalXm: swap.canRequestFinalXm,

                showEmergencyNotification: false,
                showRequestSerologyKit: swap.canRequestSerology,
                canRequestSerology: swap.canRequestSerology,
                showDonorCtStudy: true,
                showCenterAboVerification: swap.donor.centerAboUploadPath != '',
                showDonorAboMolecular: swap.donor.histogeneticsPdfAboFile != '',
                showFormsAndChecklists: true,
                swap,
                centerId: props.centerId
            };
        }

        if (swap.category == SwapCategory.PendingTransplant) {
            return {
                showPatientOutcomesButton: true,

                showAboFileButton: isDonorCenter && swap.recipient.aboUploadPath != '',
                showDemographicsFileButton: isDonorCenter && swap.recipient.demographicsUploadPath != '',
                showRecipientHlaFileButton: swap.recipient.rawTypingUploadPath != '',
                showUploadFinalXmButton: isRecipientCenter,
                showViewFinalXmButton: swap.uploadedFinalXm,
                showHlaFileButton: swap.donor.histogeneticsPdfTypingFile != '',
                showCtReportButton: isRecipientCenter && swap.donor.ctReportUploadPath != '',
                showMedChartButton: isRecipientCenter && swap.donor.medChartUploadPath != '',
                showAboVerifButton: isRecipientCenter && swap.donor.vrlAboUploadPath != '',

                showViewSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '',
                showAddSerologyButton: (swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '') == false && !showAddlSerologyButtonCriteria,
                showReuploadSerologyButton: swap.donor.serologyRecipientId == swap.recipient.id && swap.donor.serologyUploadDateTime != '' && !showAddlSerologyButtonCriteria,

                showRequestScreeningXm: isRecipientCenter,
                hasExistingScreeningXm: swap.existingScreeningXm,

                showRequestFinalXm: isRddSwap ? swap.canRequestFinalXm : (isRecipientCenter && swap.canRequestFinalXm),
                hasExistingFinalXm: swap.existingFinalXm,
                canRequestFinalXm: swap.canRequestFinalXm,

                showEmergencyNotification: swap.recipient.isTransplanted == false,
                showRequestSerologyKit: swap.canRequestSerology,
                canRequestSerology: swap.canRequestSerology,
                showDonorCtStudy: true,
                showCenterAboVerification: swap.donor.centerAboUploadPath != '',
                showDonorAboMolecular: swap.donor.histogeneticsPdfAboFile != '',
                showFormsAndChecklists: true,
                swap,
                centerId: props.centerId
            };
        }
    };

    const renderRecipient = (index: number, swap: IPendingSwap, popoverRight: any, deadlineStyle: any, actionButtons: IActionButtons, hoursLeft: number, element: JSX.Element) => {
        return (
            <>
                <RecipientRowWrapper hasCustomLogistics={props.data.logistics.logisticsOptionStatus == 1}>
                    <td>
                        {swap.swapManager && (
                            <OverlayTrigger trigger="click" overlay={element} rootClose><Button bsSize="xs" onClick={(e: any) => e.stopPropagation()}>{swap.swapManager.initials}</Button></OverlayTrigger>
                        )}
                    </td>
                    <td>{swap.swapStatus}</td>
                    <td>{swap.chainId}/{swap.clusterNumber}/{swap.positionNumber}</td>
                    <td>{swap.itemType}</td>
                    <td><CenterAlias centerId={swap.donor.centerId} centerAlias={swap.donor.center} rdnCenterId={swap.donor.rdnCenterId} rdnCenterAlias={swap.donor.rdnCenter} currentCenter={props.centerId} /></td>
                    <td><DonorAlias id={swap.donor.id} alias={swap.donor.alias} centerId={null} isDonorCenter={false} /></td>
                    <td><CenterAlias centerId={swap.recipient.centerId} centerAlias={swap.recipient.centerAlias} currentCenter={props.centerId} /></td>
                    <td><RecipientAlias id={swap.recipient.id} alias={swap.recipient.alias} centerId={swap.recipient.centerId} isRecipientCenter={true} /></td>
                    <td><SingleEpletMmRisk data={swap.singleEpletMmRisk} isAdmin={false} centerId={props.centerId} /></td>
                    <td><AlleleMatch bmtMm={swap.bmtMm} displayAsInline={false} /></td>
                    <td>
                        {swap.logisticsCallDateTime && swap.kickoffCallDateTime && (
                            <>
                                <span className='badge badge-info' style={{ color: '#123' }}>{swap.logisticsCallDateTime}</span>&nbsp;/&nbsp;<span className='badge badge-warning' style={{ color: '#123' }}>{swap.kickoffCallDateTime}</span>
                            </>
                        )}

                        {swap.logisticsCallDateTime && !swap.kickoffCallDateTime && (
                            <span className='badge badge-info' style={{ color: '#123' }}>{swap.logisticsCallDateTime}</span>
                        )}

                        {!swap.logisticsCallDateTime && swap.kickoffCallDateTime && (
                            <span className='badge badge-warning' style={{ color: '#123' }}>{swap.kickoffCallDateTime}</span>
                        )}
                    </td>
                    <td>
                        <OverlayTrigger trigger='click' rootClose placement='right' overlay={popoverRight}>
                            <Button bsStyle='link' bsSize='xs'>View</Button>
                        </OverlayTrigger>
                    </td>
                    <td>
                        <LogisticsGpsWrapper>
                            {props.data.logistics.logisticsOptionStatus == 1 && props.centerId == swap.recipient.centerId && (
                                <Button bsSize='xs' title={`Pending custom logistics (Deadline: ${swap.logistics.logisticsOptionDeadline})`} onClick={handleViewCustomLogistics}><Glyphicon glyph='alert' style={{ color: '#cf1d1d' }} /></Button>
                            )}
                            <Button bsSize='xs' onClick={handleViewLogisticsPlan}>LP</Button>
                            <Button bsSize='xs' onClick={handleViewLogistics}>L</Button>
                            <Button bsSize='xs' bsStyle={'default'} onClick={handleShowMap}>M</Button>
                            {swap.gpsAutomation != null && swap.gpsAutomation.gpsLink != '' && (
                                <Button bsSize='xs' onClick={handleShowGps}>G{swap.gpsAutomation.gpsNumber}</Button>
                            )}
                        </LogisticsGpsWrapper>
                    </td>

                    <td>
                        {swap.recipient.kidneyArrivalDateTime}
                    </td>

                    {props.show == SwapsToShow.Past && (
                        <td>
                            {swap.logistics.kidneyArrivalDateTimeActual}
                        </td>
                    )}

                    {props.show == SwapsToShow.Pending && (
                        <td>
                            {props.data.recipient.centerId == props.centerId ?
                                <>
                                    {swap.deadline != '' ?
                                        <Badge style={{ ...deadlineStyle }}>{swap.deadlineLocal}<br />({hoursLeft} hours)</Badge>
                                        :
                                        <>N/A</>
                                    }
                                </>
                                :
                                <>N/A</>
                            }
                        </td>
                    )}

                    <td>
                        <Actions
                            index={index}
                            show={props.show}
                            isInternal={false}
                            data={actionButtons}
                            pullRight={actionButtons.showDonorCtStudy}
                            matchHistoryId={props.data.matchHistoryId}
                            onItemSelected={handleActionItemSelected}
                        />
                    </td>
                </RecipientRowWrapper>
            </>
        )
    };

    const renderDonor = (index: number, swap: IPendingSwap, popoverRight: any, deadlineStyle: any, actionButtons: IActionButtons, hoursLeft: number, element: JSX.Element) => {
        let dCenterId = swap.donor.rdnCenter != '' ? swap.donor.rdnCenterId : swap.donor.centerId;
        let isDonorCenter = props.centerId == dCenterId && !props.readOnly;

        return (
            <>
                <tr key={index}>
                    <td>
                        {swap.swapManager && (
                            <OverlayTrigger trigger="click" overlay={element} rootClose><Button bsSize="xs" onClick={(e: any) => e.stopPropagation()}>{swap.swapManager.initials}</Button></OverlayTrigger>
                        )}
                    </td>
                    <td>{swap.swapStatus}</td>
                    <td>{swap.chainId}/{swap.clusterNumber}/{swap.positionNumber}</td>
                    <td>{swap.itemType}</td>
                    <td><CenterAlias centerId={swap.donor.centerId} centerAlias={swap.donor.center} rdnCenterId={swap.donor.rdnCenterId} rdnCenterAlias={swap.donor.rdnCenter} currentCenter={props.centerId} readOnly={props.readOnly} /></td>
                    <td><DonorAlias id={swap.donor.id} alias={swap.donor.alias} centerId={dCenterId} isDonorCenter={isDonorCenter} /></td>
                    <td><CenterAlias centerId={swap.recipient.centerId} centerAlias={swap.recipient.centerAlias} currentCenter={props.centerId} readOnly={props.readOnly} /></td>
                    <td><RecipientAlias id={swap.recipient.id} alias={swap.recipient.alias} centerId={swap.recipient.centerId} isRecipientCenter={swap.recipient.centerId == props.centerId && !props.readOnly} /></td>
                    <td>
                        {swap.logisticsCallDateTime && swap.kickoffCallDateTime && (
                            <>
                                <span className='badge badge-info' style={{ color: '#123' }}>{swap.logisticsCallDateTime}</span>&nbsp;/&nbsp;<span className='badge badge-warning' style={{ color: '#123' }}>{swap.kickoffCallDateTime}</span>
                            </>
                        )}

                        {swap.logisticsCallDateTime && !swap.kickoffCallDateTime && (
                            <span className='badge badge-info' style={{ color: '#123' }}>{swap.logisticsCallDateTime}</span>
                        )}

                        {!swap.logisticsCallDateTime && swap.kickoffCallDateTime && (
                            <span className='badge badge-warning' style={{ color: '#123' }}>{swap.kickoffCallDateTime}</span>
                        )}
                    </td>
                    <td>
                        <OverlayTrigger trigger='click' rootClose placement='right' overlay={popoverRight}>
                            <Button bsStyle='link' bsSize='xs'>View</Button>
                        </OverlayTrigger>
                    </td>
                    <td>
                        <LogisticsGpsWrapper>
                            {props.data.logistics.logisticsOptionStatus == 1 && props.centerId == swap.recipient.centerId && !props.readOnly && (
                                <Button bsSize='xs' title={`Pending custom logistics (Deadline: ${swap.logistics.logisticsOptionDeadline})`} onClick={handleViewCustomLogistics}><Glyphicon glyph='alert' style={{ color: '#cf1d1d' }} /></Button>
                            )}
                            {!props.readOnly && (
                                <Button bsSize='xs' onClick={handleViewLogisticsPlan}>LP</Button>
                            )}
                            <Button bsSize='xs' onClick={handleViewLogistics}>L</Button>
                            <Button bsSize='xs' bsStyle={'default'} onClick={handleShowMap}>M</Button>
                            {swap.gpsAutomation != null && swap.gpsAutomation.gpsLink != '' && (
                                <Button bsSize='xs' onClick={handleShowGps}>G{swap.gpsAutomation.gpsNumber}</Button>
                            )}
                        </LogisticsGpsWrapper>
                    </td>
                    <td>
                        {swap.donor.kidneyPickupDateTime}
                    </td>

                    {props.show == SwapsToShow.Past && (
                        <td>
                            {swap.logistics.kidneyPickupDateTimeActual}
                        </td>
                    )}

                    {props.show == SwapsToShow.Pending && (
                        <td>
                            {props.data.recipient.centerId == props.centerId ?
                                <>
                                    {swap.deadline != '' && (
                                        <Badge style={{ ...deadlineStyle }}>{swap.deadlineLocal}<br />({hoursLeft} hours)</Badge>
                                    )}
                                </>
                                :
                                <>N/A</>
                            }
                        </td>
                    )}

                    <td>
                        {!props.readOnly && (
                            <Actions
                                index={index}
                                show={props.show}
                                isInternal={false}
                                data={actionButtons}
                                pullRight={true}
                                onItemSelected={handleActionItemSelected}
                            />
                        )}
                    </td>
                </tr>
            </>
        )
    };

    const renderInternal = (index: number, swap: IPendingSwap, popoverRight: any, deadlineStyle: any, actionButtons: IActionButtons, hoursLeft: number, element: JSX.Element) => {
        return (
            <>
                <tr key={index}>
                    <td>
                        {swap.swapManager && (
                            <OverlayTrigger trigger="click" overlay={element} rootClose><Button bsSize="xs" onClick={(e: any) => e.stopPropagation()}>{swap.swapManager.initials}</Button></OverlayTrigger>
                        )}
                    </td>
                    <td>{swap.swapStatus}</td>
                    <td>{swap.chainId}/{swap.clusterNumber}/{swap.positionNumber}</td>
                    <td><DonorAlias id={swap.donor.id} alias={swap.donor.alias} centerId={props.centerId} isDonorCenter={true} /></td>
                    <td><RecipientAlias id={swap.recipient.id} alias={swap.recipient.alias} centerId={swap.recipient.centerId} isRecipientCenter={true} /></td>
                    <td>{swap.recipient.kidneyArrivalDateTime}</td>
                    <td>
                        <OverlayTrigger trigger='click' rootClose placement='right' overlay={popoverRight}>
                            <Button bsStyle='link' bsSize='xs'>View</Button>
                        </OverlayTrigger>
                    </td>
                    <td>
                        {props.show == SwapsToShow.Pending && (
                            <>
                                {props.data.recipient.centerId == props.centerId ?
                                    <>
                                        {swap.deadline != '' && (
                                            <Badge style={{ ...deadlineStyle }}>{swap.deadlineLocal}<br />({hoursLeft} hours)</Badge>
                                        )}
                                    </>
                                    :
                                    <>N/A</>
                                }
                            </>
                        )}
                    </td>

                    <td>

                    </td>

                    <td>
                        <Actions
                            index={index}
                            show={props.show}
                            isInternal={true}
                            data={actionButtons}
                            pullRight={true}
                            onItemSelected={handleActionItemSelected}
                        />
                    </td>

                    <td></td>
                </tr>
            </>
        );
    };

    const index = props.index;
    const swap = props.data;
    const hoursLeft = getHoursRemaining();
    const hoursLeftStyle = hoursLeft > 1 ? { fontWeight: 700, backgroundColor: '#00FF00', color: 'black' } : { fontWeight: 700, backgroundColor: '#FF6666', color: 'white' };
    const deadlineStyle = swap.recipient != null && swap.recipient.centerId == props.centerId ? hoursLeftStyle : {};

    const popoverRight = (
        <Popover id={'tubes-right-' + swap.chainId + '/' + swap.clusterNumber + '/' + swap.positionNumber}>
            {props.data.tubes.map((tube, index) =>
                <div key={index}>{tube}</div>
            )}
        </Popover>
    );

    const swapManagerOverlay = (
        <Popover>
            <div>{swap.swapManager ? swap.swapManager.name : ''}</div>
            <div>{swap.swapManager ? swap.swapManager.phoneNumber : ''}</div>
            <div>{swap.swapManager ? swap.swapManager.email : ''}</div>
        </Popover>
    );

    const actionButtons = getActionButtons();

    if (props.type == PendingSwapsListType.Recipient) {
        return renderRecipient(index, swap, popoverRight, deadlineStyle, actionButtons, hoursLeft, swapManagerOverlay);
    }

    if (props.type == PendingSwapsListType.Donor) {
        return renderDonor(index, swap, popoverRight, deadlineStyle, actionButtons, hoursLeft, swapManagerOverlay);
    }

    if (props.type == PendingSwapsListType.Remote) {
        return renderDonor(index, swap, popoverRight, deadlineStyle, actionButtons, hoursLeft, swapManagerOverlay);
    }

    return renderInternal(index, swap, popoverRight, deadlineStyle, actionButtons, hoursLeft, swapManagerOverlay);
}

export default PendingSwapsRow;
